import {
    Component,
    OnInit,
    ViewEncapsulation,
    ChangeDetectorRef,
    ViewChildren,
    QueryList, Input, Output, EventEmitter, AfterViewInit, DestroyRef, inject
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {BehaviorSubject, combineLatestWith, Observable} from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { LanguageDialogComponent } from '@emm/shared/dialogs/language-dialog/language-dialog.component';
import { SharedFacadeService } from '@emm/shared/services/shared-facade.service';
import { itemsFormFieldMap as fieldMap } from '@emm/shared/formly/maps/items-form.config';
import { MatDialog } from '@angular/material/dialog';
import { DynamicFormFieldService } from '@emm/shared/services/dynamic-form-field.service';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Item} from '@emm/core/models/item.model';
import {Locale} from '@emm/core/models/locale.model';
import {ItemAttributeType} from '@emm/core/models/enums';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {ItemGroupingsFacadeService} from '@emm/management/services/item-groupings-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {
    DynamicManagementFormComponent
} from '@emm/shared/components/dynamic-form/dynamic-management-form/dynamic-management-form.component';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';

const LEGEND_ITEMS = [{ color: '#B5E9FD', label: 'Global Value' }, { color: '#EBF9FF', label: 'Overridden Value' }];
const DARK_MODE_LEGEND_ITEMS = [{ color: '#4A4A4A', label: 'Global Value' }, { color: '#5B6770', label: 'Overridden Value' }];
const SUBMIT_ORIGIN = 'panel';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-items-form',
    styleUrls: ['./items-form.component.scss'],
    templateUrl: './items-form.component.html',
    providers: [DynamicFormFieldService,],
    standalone: false
})
export class ItemsFormComponent implements OnInit, AfterViewInit {
    @Input() selectedItem: Item;
    removeMealFormfieldToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.REMOVE_MEAL_FORMFIELD);
    isDarkMode = false;
    attributesByGroup = new Map<string, BehaviorSubject<any>>();
    attributeGroups;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    isReadOnly$ = this.authFacade.isReadOnly$;
    disabled$ = this.isReadOnly$.pipe(combineLatestWith(this.itemsFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    form = this.itemsFacade.form$;
    fields = [];
    values = [];
    isNameIncluded = false;
    item: {
        disabled: boolean,
        itemClass: string,
        itemType: string,
        itemTypeName: string,
        lastUpdatedBy: string,
        lastUpdatedDate: string,
        locked: boolean,
        name: string,
        tag: string
    };
    attributesExcludedFromForms = ['DISABLED', 'MEAL', 'LOCKED'];
    itemDetail$ = this.itemsFacade.itemDetail$;
    itemLocaleDetail$ = this.itemsFacade.itemLocaleDetail$;
    itemTag: string;
    legendItems = LEGEND_ITEMS;
    locale: Locale;
    model: any;
    pipe = new DatePipe('en-US');
    renderForm = new BehaviorSubject<boolean>(true);
    selectedStatus: string;
    @Input() previousAttributeGroups: AttributeGroup[];
    @Output() attributeGroupSelected = new EventEmitter<AttributeGroup[]>();
    @ViewChildren(DynamicManagementFormComponent) dynamicForms: QueryList<DynamicManagementFormComponent>;
    model$ = this.itemLocaleDetail$.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(RA.isNotNil),
        filter((attributes: any) => RA.isNonEmptyArray(attributes)),
        withLatestFrom(this.sharedFacade.localeSelected$),
        map(([attributes, locale]) => {
            this.values = [];
            attributes.filter(attribute => attribute.tag !== 'DISABLED' && attribute.tag !== 'LOCKED').forEach(attribute => {
                const isBoolean = RA.isBoolean(attribute.values[0].value);
                this.values.push({
                    key: attribute.jsonProperty,
                    value: isBoolean ? String(attribute.values[0].value) : attribute.values[0].value,
                    disabled: attribute.metadata.readOnly,
                    required: attribute.metadata.required,
                    controlType: attribute.metadata.type,
                    description: attribute.description,
                    metadata: attribute.metadata,
                    values: attribute.values,
                    name: attribute.name,
                    tag: attribute.tag,
                })
            });
            if (!this.item) {
                return {};
            }
            return attributes.filter(attribute => attribute.tag !== 'DISABLED' && attribute.tag !== 'LOCKED')
                .reduce((accumulate, attribute) => {
                    const values = attribute.values[0];
                    const isBoolean = RA.isBoolean(values.value);
                    return {
                        ...accumulate,
                        [attribute.tag]: isBoolean ? String(values.value) : values.value
                    };
                },
                {
                    countryCode: locale.country,
                    itemTypeTag: this.item.itemType,
                    language: locale.language,
                    locked: this.item.locked.toString(),
                    name: this.item && this.item.name
                }
            );
        }),
        tap(model => this.model = model)
    );

    fields$ = this.itemLocaleDetail$.pipe(
        filter(RA.isNonEmptyArray),
        withLatestFrom(this.isReadOnly$, this.sharedFacade.localeSelected$, this.removeMealFormfieldToggle$),
        map(([attributes, isReadOnly, localeSelected, removeMealToggle]) => {
            return [
                ...attributes
                    .filter(attribute => attribute.tag !== 'DISABLED' && attribute.tag !== 'LOCKED')
                    .map(attribute => ({...attribute}))
                    .filter(({ metadata: { type }}) => Object.values(ItemAttributeType).includes(type))
                    .filter(({ metadata: { type }}) => Object.keys(fieldMap).includes(type))
                    .map((attribute, index: number) => {
                        attribute.auditInfo = `Last updated by ${attribute.values[0].updatedBy}`
                            + ` on ${this.pipe.transform(attribute.values[0].dateUpdated, MenuDateUtil.dateFormat)}`
                            + ` at ${this.pipe.transform(attribute.values[0].dateUpdated, MenuDateUtil.timeFormat)}`;
                        return {
                            attribute,
                            config: fieldMap[attribute.metadata.type](attribute, index,
                                (isReadOnly || attribute.metadata.readOnly), localeSelected.language)
                        };
                    })
                    .map((field) => {
                        return {
                            ...field.config,
                            templateOptions: {
                                ...field.config.templateOptions,
                                localeSupport: field.attribute.metadata.localeSupport,
                                showLocaleIcons: true,
                                editLocales: () => {
                                    this.dialog.open(LanguageDialogComponent, {
                                        data: {
                                            attribute: field.attribute,
                                            itemTag: this.item.tag
                                        },
                                        disableClose: true,
                                        position: { top: '100px' },
                                        width: '470px'
                                    });
                                }
                            }
                        };
                    })
            ];
        })
    );


    saved$ = this.sharedFacade.saved$
        .pipe(
            withLatestFrom(this.sharedFacade.origin$),
            takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(([saved, origin]) => {
            if (this.itemTag && saved && origin === SUBMIT_ORIGIN) {
                this.sharedFacade.resetSaved();
                if (this.isNameIncluded) {
                    this.itemsFacade.init();
                    this.itemsFacade.selectItem(this.itemTag);
                }
            }
        });

    constructor(
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private itemsFacade: ItemsFacadeService,
        private itemGroupingsFacade: ItemGroupingsFacadeService,
        private notificationService: NotificationService,
        public sharedFacade: SharedFacadeService,
        private changeDetector: ChangeDetectorRef,
        private api: ManagementApiService,
        private launchDarklyService: LaunchDarklyService,
    ) { }



    ngOnInit(): void {
        this.sharedFacade.currentTheme$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(theme => {
            theme === 'dark-theme' ? this.isDarkMode = true : this.isDarkMode = false;
            this.legendItems = this.isDarkMode ? DARK_MODE_LEGEND_ITEMS : LEGEND_ITEMS;
        });
        if (this.itemGroupingsFacade.itemDetail$.value !== null && window.location.pathname.includes('item-groupings')) {
            this.itemDetail$ = this.itemGroupingsFacade.itemDetail$;
        }
        // Init item, US to populate first.
        this.itemDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
            if (item) {
                this.item = {
                    disabled: item.disabled,
                    itemClass: item.itemClass,
                    itemType: item.itemType,
                    itemTypeName: item.itemTypeName,
                    lastUpdatedBy: item.lastUpdatedBy,
                    lastUpdatedDate: item.lastUpdatedDate,
                    locked: item.locked,
                    name: item.name,
                    tag: item.tag
                };
                this.sharedFacade.init();
            }
        });
        this.itemLocaleDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.getAttributeGroups();
        })

        // Locale selected, get fields.
        this.sharedFacade.localeSelected$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({country, language}) => {
                this.destroyForm().then();
                if (!this.item || !this.item.tag || !country) {
                    return;
                }
                this.getLocale(this.item.tag, country, language)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(result => {
                        this.itemLocaleDetail$.next(result.filter(attribute => attribute.tag !== 'DISABLED' &&
                            attribute.tag !== 'LOCKED'));
                            this.initializeLocaleForm();
                        }
                    );
            });


        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(model => this.model = model);
        this.fields$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
            this.fields = [];
            for (let i = 0; i < result.length; i++) {
                const key = result[i].key;
                const field = {
                    name: result[i].templateOptions.label,
                    value: result[i]['value'] ? result[i]['value'] : '',
                    key,
                    label: result[i].templateOptions.label,
                    required: result[i].templateOptions.required,
                    order: i,
                    disabled: result[i].templateOptions.readonly,
                    controlType: result[i].type,
                    type: result[i].type,
                    options: result[i].templateOptions.options,
                };
                this.fields.push(field);
            }
        });
    }


    ngAfterViewInit() {
        this.dynamicForms.changes
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
            this.changeDetector.detectChanges();
        });
    }

    cancel(attributeGroupId: string): void {
        const dynamicForm = this.dynamicForms.find(df => df.attributeGroupId === attributeGroupId)
        this.model = dynamicForm.model
        dynamicForm.form.reset(this.model)
        dynamicForm.form.markAsPristine()
    }

    cancelAllDirtyAttributeGroups(): void {
        if (this.dynamicForms) {
            this.dynamicForms.forEach(df => {
                if (df.form.dirty) {
                    this.cancel(df.attributeGroupId)
                }
            });
        }
    }

    submitAllDirtyAttributeGroups(itemTag: string): void {
        try {
            if (this.renderForm && this.dynamicForms) {
                if (this.dynamicForms.first.form.dirty) {
                    if (this.dynamicForms.first.form.controls['name'] &&
                        this.dynamicForms.first.form.controls['name'].dirty && this.item.itemType === 'SUBMENU' &&
                        this.model['language'] !== 'es') {
                        this.renameSubmenu(this.item.tag, this.dynamicForms.first.form.controls['name'].value);
                    }
                }
            }
        } catch (e) {
            this.notificationService.error(`Could not save the ${itemTag}. Please try again.`);
        }
        this.submit(itemTag);
    }

    submit(itemTag: string, attributeGroupId?: string): void {
        if (this.dynamicForms.first.form.invalid || this.hasInvalidSubsGroupId()) {
            if (this.hasInvalidSubsGroupId()) {
                this.notificationService.error(`If an item has an RMI, it must also have a Subs Group ID`);
                return;
            }
            this.notificationService.error(`Please fill out all required fields.`);
            return;
        }
        this.itemTag = itemTag;
        const allDirtyFields = [];
        this.dynamicForms
            .filter(df => df.form.dirty && (!attributeGroupId || df.attributeGroupId === attributeGroupId))
            .forEach(df => {
                allDirtyFields.push(...Object.keys(df.form.controls)
                    .filter(field => df.form.controls[field].dirty)
                    .map(key => ({
                        country: this.model['countryCode'] ? this.model['countryCode'] : 'US',
                        language: this.model['language'] ? this.model['language'] : '',
                        tag: this.values.find(object => object.key === key).tag,
                        value: df.form.controls[key].value,
                        reset: this.model[key] === ''
                    }))
                );
                df.form.markAsPristine();
            });

        if (allDirtyFields.length) {
            const excludedAttributes = ['MEAL'];
            this.isNameIncluded = allDirtyFields.some(field => field.tag === 'NAME');
            const hasExcludedAttributes = allDirtyFields.some(field => excludedAttributes.includes(field.tag));
            if (hasExcludedAttributes) {
                this.showCustomSnackbar();
            }
            const dirtyFieldsExcludingMeal = allDirtyFields.filter(field => field.tag !== 'MEAL')
            this.sharedFacade.saveAttributeLocaleFields(itemTag, dirtyFieldsExcludingMeal, SUBMIT_ORIGIN);
        }

        this.itemGroupingsFacade.updateNodeInItemGrouping(itemTag);
    }

    showCustomSnackbar() {
        const customSnackbar = document.getElementById('customSnackbar');
        customSnackbar.className = 'show';
        setTimeout(() => {
            customSnackbar.className = customSnackbar.className.replace('show', '');
        }, 5000);
    }

    closeCustomSnackbar() {
        const customSnackbar = document.getElementById('customSnackbar');
        customSnackbar.className = customSnackbar.className.replace('show', '');
    }


    hasChanges(): boolean {
        let result = false;
        if (this.renderForm && this.dynamicForms) {
            result = this.dynamicForms.some((df) => df.form.dirty === true);
        }
        return result;
    }

    hasInvalidImageLink(): boolean {
        let result = false;
        if (this.renderForm && this.dynamicForms) {
            const imageForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'IMAGE');
            result = imageForm?.form?.invalid;
        }
        return result;
    }

    hasInvalidSubsGroupId(): boolean {
        let result = false;
        if (this.renderForm && this.dynamicForms) {
            const coreForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'CORE');
            result = coreForm?.form?.invalid;
        }
        return result;
    }

    hasInvalidFormfield() {
        return !!(this.hasInvalidImageLink() || this.hasInvalidSubsGroupId());
    }

    getLocale(tag, country, language) {
        return this.itemsFacade.getItemLocale(tag, country, language);
    }

    async destroyForm() {
        await this.renderForm.next(false);
    }

    initializeLocaleForm() {
        this.renderForm.next(true);
    }


    getAttributeGroups(): void {
        try {
            this.api.getAllAttributeGroups()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                        next: (res) => {
                            this.attributeGroups = res;
                            res.forEach(group => {
                                this.attributesByGroup.set(group.id, this.itemsFacade.filterByAttributeGroup(group.id))
                            });
                            this.attributeGroups = this.attributeGroups.filter(group => group.id !== 'DYNAMIC');
                        },
                        error: (error) => {
                            this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        }
                    }
                );
        } catch (e) {}
    }


    hasValue(attributeGroupId: string): boolean {
        const attributesByGroup = this.attributesByGroup.get(attributeGroupId)
        return attributesByGroup?.getValue().length !== 0
    }


    openPanel(status): void {
        this.selectedStatus = status;
        try {
            if (!this.isPreviousPanel(status)) {
                this.previousAttributeGroups.push(status);
            }
            this.attributeGroupSelected.next(this.previousAttributeGroups);
        } catch (e) {}
    }


    closePanel(status): void {
        this.selectedStatus = null;
        this.previousAttributeGroups?.find((item, index) => item === status && this.previousAttributeGroups.splice(index, 1));
        this.attributeGroupSelected.next(this.previousAttributeGroups);
    }

    isPreviousPanel(status) {
        try {
            return this.previousAttributeGroups.some(i => i.name === status.name);
        } catch (e) {
            return false;
        }
    }

    renameSubmenu(submenuId: string, newName: string, effectiveDate?: string) {
        this.api.renameSubmenu(submenuId, newName, effectiveDate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: () => {
                    this.notificationService.success(`Successfully renamed ${submenuId}`);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            }
        );
    }
}
